import React, { useState, useRef, useEffect } from 'react';
import { Calendar, ChevronDown } from 'lucide-react';
import { formatDate } from '../../utils/formatters';

const DateRangePicker = ({ value, onChange, campaignStart, campaignEnd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState('campaign');
  const dropdownRef = useRef(null);

  // YouTube API Compliance: Limit date ranges to 30 days max
  const MAX_DAYS = 30;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const presets = [
    {
      id: 'campaign',
      label: 'Campaign Duration',
      getValue: () => ({
        startDate: campaignStart ? new Date(campaignStart) : null,
        endDate: campaignEnd ? new Date(campaignEnd) : null
      })
    },
    {
      id: 'last7',
      label: 'Last 7 Days',
      getValue: () => ({
        startDate: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000),
        endDate: new Date()
      })
    },
    {
      id: 'last30',
      label: 'Last 30 Days',
      getValue: () => ({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000),
        endDate: new Date()
      })
    },
    {
      id: 'thisMonth',
      label: 'This Month',
      getValue: () => {
        const now = new Date();
        return {
          startDate: new Date(now.getFullYear(), now.getMonth(), 1),
          endDate: new Date()
        };
      }
    },
    {
      id: 'lastMonth',
      label: 'Last Month',
      getValue: () => {
        const now = new Date();
        return {
          startDate: new Date(now.getFullYear(), now.getMonth() - 1, 1),
          endDate: new Date(now.getFullYear(), now.getMonth(), 0)
        };
      }
    },
    {
      id: 'all',
      label: 'All Time',
      getValue: () => ({ startDate: null, endDate: null })
    }
  ];

  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    onChange(preset.getValue());
    setIsOpen(false);
  };

  const handleCustomDateChange = (field, dateString) => {
    const newValue = { ...value };
    newValue[field] = dateString ? new Date(dateString) : null;
    onChange(newValue);
    setSelectedPreset('custom');
  };

  const getDisplayText = () => {
    if (selectedPreset === 'all' || (!value.startDate && !value.endDate)) {
      return 'All Time';
    }
    
    const preset = presets.find(p => p.id === selectedPreset);
    if (preset && selectedPreset !== 'custom') {
      return preset.label;
    }
    
    if (value.startDate && value.endDate) {
      return `${formatDate(value.startDate, 'short')} - ${formatDate(value.endDate, 'short')}`;
    }
    
    return 'Select dates';
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        <Calendar className="w-4 h-4 mr-2 text-gray-500" />
        {getDisplayText()}
        <ChevronDown className={`w-4 h-4 ml-2 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-72 bg-white rounded-xl shadow-lg border border-gray-200 z-50">
          {/* Presets */}
          <div className="p-2 border-b border-gray-100">
            <p className="px-3 py-2 text-xs font-medium text-gray-500 uppercase">Quick Select</p>
            <div className="space-y-1">
              {presets.map(preset => (
                <button
                  key={preset.id}
                  onClick={() => handlePresetSelect(preset)}
                  className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                    selectedPreset === preset.id
                      ? 'bg-sc-pink/10 text-sc-pink'
                      : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>

          {/* Custom date inputs */}
          <div className="p-4">
            <p className="text-xs font-medium text-gray-500 uppercase mb-3">Custom Range</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Start Date</label>
                <input
                  type="date"
                  value={value.startDate ? value.startDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleCustomDateChange('startDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">End Date</label>
                <input
                  type="date"
                  value={value.endDate ? value.endDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => handleCustomDateChange('endDate', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-sc-pink focus:border-sc-pink"
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangePicker;
